<template>
  <DefaultLayout :disableFacilitatorTools="true">
    <base-alert type="danger" v-if="saveError">
      <span class="alert-inner--icon"><i class="ni ni-notification-70"></i></span>
      <span class="alert-inner--text">{{ $t("save-error") }} {{ saveErrorMessage }}</span>
      <button type="button" class="close" data-dismiss="alert" aria-label="Close"></button>
    </base-alert>

    <br />

    <h4 v-if="createNew">
      {{ $t("create-new") }}
    </h4>
    <h4 v-else>
      {{ $t("edit-simulation") }}
    </h4>

    <base-button
      v-if="ready"
      size="sm"
      type="primary"
      style="white-space: normal; margin: 2px"
      v-on:click="$router.push('/admin/editor')"
      >{{ $t(exitButtonText) }}
    </base-button>

    <b-toast
      id="save-successful-toast"
      no-close-button
      b-toaster-top-right
      variant="success"
      :solid="true"
    >
      <strong>
        {{ $t("save-successful") }}
      </strong>
    </b-toast>

    <span v-if="ready && getRPSScenarioLocales.length">
      <br />
      <base-button
        v-if="ready"
        @click="save()"
        type="success"
        style="margin: 5px"
        v-b-tooltip.hover
        :disabled="disableSave()"
        :title="saveButtonHover()"
        >{{ $t("save-changes") }}</base-button
      >

      <br />
      <br />

      <base-button
        v-if="ready"
        style="margin: 5px"
        type="primary"
        size="sm"
        @click="cancel()"
        :disabled="disableCancel()"
        >{{ $t("cancel-changes") }}</base-button
      >
    </span>

    <template v-if="ready && getRPSScenarioLocales.length">
      <br />
      {{ $t("choose-locale") }}<br />

      <span v-for="locale in getRPSScenarioLocales">
        <base-button
          :key="localeValidityKey"
          size="md"
          @click="changeLocale(locale)"
          :class="!isValid(locale) ? 'error' : ''"
          :type="locale === selectedLocale ? 'success' : ''"
          >{{ getLanguage(locale) }}</base-button
        >
      </span>
    </template>

    <br />
    <br />

    <base-dropdown v-if="localesWhichCanBeAdded.length && ready">
      <base-button
        slot="title"
        type="secondary"
        class="dropdown-toggle"
        v-b-tooltip.hover
        :title="$t('add-new-language-hover')"
        >{{ $t("add-new-language") }}
      </base-button>
      <div v-for="locale in localesWhichCanBeAdded">
        <a class="dropdown-item" @click="addLocaleDialog(locale)">{{ getLanguage(locale) }}</a>
      </div>
    </base-dropdown>

    <br />
    <div v-if="translating">
      <base-progress
        type="default"
        :label="$t('translating')"
        :value="Math.round(100 * (translatedItems / translatableItems))"
      ></base-progress>
    </div>
    <div class="spinner-border" role="status" v-if="!ready" />

    <template v-if="ready && getRPSScenarioLocales.length">
      <br />

      <small class="text-uppercase"
        ><strong>{{ $t("begin-end") }}</strong></small
      >
      <br />

      <span v-for="locale in getRPSScenarioLocales" v-show="locale === selectedLocale">
        <br />

        <RPSScenario
          :rpsScenario="getRPSScenarioInSpecifiedLocale(locale)"
          :otherLocales="rpsScenarioOtherLocales()"
        />
      </span>

      <hr />
    </template>

    <small v-if="ready && rpsCases.length" class="text-uppercase"
      ><strong>{{ $t("cases") }}</strong></small
    >

    <span v-for="locale in getRPSScenarioLocales" v-show="locale === selectedLocale">
      <TransitionGroup 
        name="list" 
        tag="div"
        v-bind:css="false"
        @enter="simpleEnter"
        @leave="simpleLeave"
      >
        <div
          v-for="rpsCase in getRPSCasesInSpecifiedLocale(locale)" :key="rpsCase.id || rpsCase.oid"
        >
          <RPSCaseField
            :rpsCase="rpsCase"
            :showUp="showUpRpsCase(rpsCase)"
            :showDown="showDownRpsCase(rpsCase)"
            :showDelete="showDeleteRpsCase()"
            :otherLocales="rpsCaseOtherLocales(rpsCase)"
          />
          <small class="text-uppercase"
            ><strong>{{ $t("answer-options") }}</strong></small
          >
          &nbsp;<base-button
            tabindex="-1"
            @click="showAnswerOptionsHelp = !showAnswerOptionsHelp"
            size="sm"
            type="primary"
            >{{ $t("example") }}</base-button
          >
          <br />
          <EditorHelpImage type="rps-case-answer-options" v-if="showAnswerOptionsHelp" />

          <TransitionGroup 
            name="list" 
            tag="div"
            v-bind:css="false"
            @before-enter="beforeEnterChoice"
            @enter="enterChoice"
            @leave="leaveChoice"
          >
            <div
              v-for="rpsCaseChoice in getRPSCaseChoicesInSpecifiedLocale(
                rpsCase.case_number,
                locale
              )" :key="rpsCaseChoice.id || rpsCaseChoice.oid"
            >
              <RPSCaseChoiceField
                :rpsCaseChoice="rpsCaseChoice"
                :showUp="showUpRpsCaseChoice(rpsCaseChoice)"
                :showDown="showDownRpsCaseChoice(rpsCase, rpsCaseChoice)"
                :showDelete="showDeleteRpsCaseChoice(rpsCase)"
                :otherLocales="rpsCaseChoiceOtherLocales(rpsCaseChoice)"
              />
            </div>
          </TransitionGroup>

          <div class="container" v-if="showAddRpsCaseChoice(rpsCase)">
            <div class="row">
              <div class="col-10"></div>
              <div class="col-2">
                <base-button tabindex="-1" size="sm" @click="addRpsCaseChoice(rpsCase)">{{
                  $t("add-option")
                }}</base-button>
              </div>
            </div>
          </div>

          <br />
          <hr />
        </div>
      </TransitionGroup>
    </span>
    <br />
    <base-button tabindex="-1" size="sm" @click="addRpsCase()" v-if="showAddRpsCase()">{{
      $t("add-case")
    }}</base-button>

    <base-button
      tabindex="-1"
      size="sm"
      @click="modalTemplate = true"
      v-if="showAddRpsCaseFromTemplate()"
      >{{ $t("add-case-from-templates") }}</base-button
    >

    <br />

    <EditorTemplateDialog v-if="modalTemplate" :locale="selectedLocale" />

    <div v-if="ready && getRPSScenarioLocales.length">
      <br />
      <base-button
        v-if="ready"
        size="sm"
        type="primary"
        style="white-space: normal; margin: 2px"
        v-on:click="$router.push('/admin/editor')"
        >{{ $t(exitButtonText) }}
      </base-button>
      <br />
      <base-button
        v-if="ready"
        @click="save()"
        type="success"
        v-b-tooltip.hover
        :disabled="disableSave()"
        :title="saveButtonHover()"
        >{{ $t("save-changes") }}</base-button
      >
    </div>
  </DefaultLayout>
</template>

<script>
import Swal from "sweetalert2";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import {
  getRPSCases,
  getRPSCaseChoices,
  editRPSScenario,
  getRPSScenario,
  translate,
  getRPSTemplateCategories,
  getRPSTemplateCases,
  getRPSTemplateCaseChoices,
  getRPSScenariosTemplates,
  getRPSCasesTemplates,
  getRPSCaseChoicesTemplates
} from "@/services/APIService";
import RPSCaseField from "@/components/RPSCaseField.vue";
import RPSCaseChoiceField from "@/components/RPSCaseChoiceField.vue";
import RPSScenario from "@/components/RPSScenario.vue";
import EditorTemplateDialog from "@/components/rps/EditorTemplateDialog.vue";
import Vue from "vue";
import EditorHelpImage from "@/components/EditorHelpImage.vue";

const isEqual = require("lodash.isequal");
const cloneDeep = require("lodash.clonedeep");
const sortby = require("lodash.sortby");
const difference = require("lodash.difference");

export default {
  components: {
    DefaultLayout,
    RPSCaseField,
    RPSCaseChoiceField,
    RPSScenario,
    EditorHelpImage,
    EditorTemplateDialog,
  },
  data() {
    return {
      selectedRPSScenario: null,
      rpsCases: [],
      originalRPSCases: null,
      rpsCaseChoices: [],
      originalRPSCaseChoices: null,
      showAlert: false,
      selectedLocale: null,
      selectedRPSScenarioLocales: [],
      originalSelectedRPSScenarioLocales: [],
      localeValidityKey: 0,
      createNew: false,
      saveError: false,
      saveErrorString: "",
      ready: false,
      showAnswerOptionsHelp: false,
      translatableItems: 0,
      translatedItems: 0,
      translating: false,
      modalTemplate: false,
      templateCategories: [],
      rpsScenariosTemplates: []
    };
  },
  beforeDestroy() {
    this.$bus.$off("rps-case-choice-down");
    this.$bus.$off("rps-case-choice-up");
    this.$bus.$off("rps-case-choice-delete");
    this.$bus.$off("rps-case-down");
    this.$bus.$off("rps-case-up");
    this.$bus.$off("rps-case-delete");
    this.$bus.$off("close-template-dialog");
    this.$bus.$off("add-case-from-template");
    this.clearInterval();
  },
  async mounted() {
    this.templateCategories = (await getRPSTemplateCategories()).data;
    this.rpsScenariosTemplates = (await getRPSScenariosTemplates()).data;

    const scenarioToEdit = parseInt(this.$route.params.rpsscenarioid);

    if (scenarioToEdit) {
      this.startEditorTimeout();
      // If not set, creating new simulation
      if (this.$route.params.saved) {
        this.$bvToast.show("save-successful-toast");
      }

      await this.selectScenario(scenarioToEdit);

      if (this.$route.params.locale) {
        this.selectedLocale = this.$route.params.locale;
      }
    } else {
      this.createNew = true;
    }

    this.ready = true;

    this.$bus.$on("close-template-dialog", () => {
      this.modalTemplate = false;
    });

    this.$bus.$on("add-case-from-template", async (id, type) => {
      let cases = null;
      let caseChoices = null;

      if (type === "template") {
        cases = (await getRPSTemplateCases()).data;
        caseChoices = (await getRPSTemplateCaseChoices()).data;
      } else {
        // RPS
        cases = (await getRPSCasesTemplates()).data;
        caseChoices = (await getRPSCaseChoicesTemplates()).data;
      }

      const rpsCases = this.getRPSCasesInLocale;

      const caseNumber = rpsCases.length + 1;

      for (let i = 0; i < cases.length; i++) {
        if (type === "template") {
          if (id !== cases[i].id) {
            continue;
          }
        } else if (id !== cases[i].rps_case || cases[i].locale !== this.selectedLocale) {
          continue;
        }

        const c = cases[i];

        const newRpsCase = {
          locale: this.selectedLocale,
          case_number: caseNumber,
          description: c.description,
          title: c.title,
          case_question: c.case_question,
          description_above_cases: c.description_above_cases,
          description_below_cases: c.description_below_cases,
          facilitator_instructions: c.facilitator_instructions,
          oid: Math.random()
        };

        if (type === "template") {
          newRpsCase.rps_template_case = c.id;
        } else {
          newRpsCase.rps_case = c.rps_case;
        }

        this.rpsCases.push(newRpsCase);
        break;
      }

      // Let's add empty rps case for all other locales:
      const locales = this.getRPSScenarioLocales;
      for (let i = 0; i < locales.length; i++) {
        if (locales[i] !== this.selectedLocale) {
          this.pushEmptyRPSCase(locales[i], caseNumber);
        }
      }

      for (let i = 0; i < caseChoices.length; i++) {
        if (type === "template") {
          if (id !== caseChoices[i].rps_template_case) {
            continue;
          }
        } else if (
          id !== caseChoices[i].rps_case ||
          caseChoices[i].locale !== this.selectedLocale
        ) {
          continue;
        }

        const c = caseChoices[i];
        const newRpsCaseChoice = {
          locale: this.selectedLocale,
          rps_case: null,
          order_number: c.order_number,
          choice_text: c.choice_text,
          case_number: caseNumber,
          oid: Math.random()
        };
        this.rpsCaseChoices.push(newRpsCaseChoice);

        // And for all other locales:
        for (let j = 0; j < locales.length; j++) {
          if (locales[j] !== this.selectedLocale) {
            const newChoice = cloneDeep(newRpsCaseChoice);

            newChoice.locale = locales[j];
            newChoice.choice_text = "";
            newChoice.oid = Math.random();

            this.rpsCaseChoices.push(newChoice);
          }
        }
      }
    });

    this.$bus.$on("rps-case-choice-down", rpsCaseChoice => {
      const order_number = rpsCaseChoice.order_number;
      const case_number = rpsCaseChoice.case_number;

      for (let i = 0; i < this.rpsCaseChoices.length; i++) {
        const rcc = this.rpsCaseChoices[i];

        if (rcc.case_number === case_number && rcc.order_number === order_number) {
          rcc.order_number = order_number + 1;
          continue;
        }

        if (rcc.case_number === case_number && rcc.order_number === order_number + 1) {
          rcc.order_number = order_number;
        }
      }
    });

    this.$bus.$on("rps-case-choice-up", rpsCaseChoice => {
      const order_number = rpsCaseChoice.order_number;
      const case_number = rpsCaseChoice.case_number;

      for (let i = 0; i < this.rpsCaseChoices.length; i++) {
        const rcc = this.rpsCaseChoices[i];

        if (rcc.case_number === case_number && rcc.order_number === order_number) {
          rcc.order_number = order_number - 1;
          continue;
        }

        if (rcc.case_number === case_number && rcc.order_number === order_number - 1) {
          rcc.order_number = order_number;
        }
      }
    });

    this.$bus.$on("rps-case-choice-delete", rpsCaseChoice => {
      const order_number = rpsCaseChoice.order_number;
      const case_number = rpsCaseChoice.case_number;

      do {
        const index = this.rpsCaseChoices.findIndex(
          rcc => rcc.order_number === order_number && rcc.case_number === case_number
        );

        if (index === -1) {
          break;
        }

        this.rpsCaseChoices.splice(index, 1);
        // eslint-disable-next-line no-constant-condition
      } while (true);

      // Let's rearrange rest:
      const choices = this.rpsCaseChoices;
      for (let i = 0; i < choices.length; i++) {
        if (choices[i].case_number === case_number && choices[i].order_number > order_number) {
          choices[i].order_number--;
        }
      }
    });

    this.$bus.$on("rps-case-down", rpsCase => {
      const case_number = rpsCase.case_number;

      for (let i = 0; i < this.rpsCases.length; i++) {
        const rc = this.rpsCases[i];

        if (rc.case_number === case_number) {
          Vue.set(rc, "case_number", case_number + 1);
          continue;
        }

        if (rc.case_number === case_number + 1) {
          Vue.set(rc, "case_number", case_number);
        }
      }

      // Let's rearrange rps case choices:
      const choices = this.rpsCaseChoices;
      for (let i = 0; i < choices.length; i++) {
        const c = choices[i];

        if (c.case_number === case_number) {
          Vue.set(c, "case_number", case_number + 1);
          continue;
        }

        if (c.case_number === case_number + 1) {
          Vue.set(c, "case_number", case_number);
        }
      }
    });

    this.$bus.$on("rps-case-up", rpsCase => {
      const case_number = rpsCase.case_number;

      for (let i = 0; i < this.rpsCases.length; i++) {
        const rc = this.rpsCases[i];

        if (rc.case_number === case_number) {
          Vue.set(rc, "case_number", case_number - 1);
          continue;
        }

        if (rc.case_number === case_number - 1) {
          Vue.set(rc, "case_number", case_number);
        }
      }

      // Let's rearrange rps case choices:
      const choices = this.rpsCaseChoices;
      for (let i = 0; i < choices.length; i++) {
        const c = choices[i];

        if (c.case_number === case_number) {
          Vue.set(c, "case_number", case_number - 1);
          continue;
        }

        if (c.case_number === case_number - 1) {
          Vue.set(c, "case_number", case_number);
        }
      }
    });

    this.$bus.$on("rps-case-delete", rpsCase => {
      const case_number = rpsCase.case_number;

      // Let's first delete corresponding case choices:
      this.rpsCaseChoices = this.rpsCaseChoices.filter(
        choice => choice.case_number !== case_number
      );
      // And then rps case
      this.rpsCases = this.rpsCases.filter(rc => rc.case_number !== case_number);

      // Let's rearrange rest:
      const cases = this.rpsCases;

      for (let i = 0; i < cases.length; i++) {
        if (cases[i].case_number > case_number) {
          cases[i].case_number--;
        }
      }

      // Let's rearrange rps case choices:
      const allChoices = this.rpsCaseChoices;
      for (let i = 0; i < allChoices.length; i++) {
        const c = allChoices[i];

        if (c.case_number > case_number) {
          Vue.set(c, "case_number", c.case_number - 1);
        }
      }
    });
  },
  computed: {
    exitButtonText() {
      if (this.disableSave()) {
        return "exit";
      } else {
        return "exit-discard";
      }
    },
    getRPSScenarioLocales() {
      const locales = [];

      this.selectedRPSScenarioLocales.forEach(aCase => locales.push(aCase.locale));

      // Filter unique:
      return [...new Set(locales)];
    },
    getRPSCasesInLocale() {
      return this.getRPSCasesInSpecifiedLocale(this.selectedLocale);
    },
    localesWhichCanBeAdded() {
      const existingLocales = this.getRPSScenarioLocales;
      const possibleLocales = this.$store.state.main.allLangs;
      return difference(possibleLocales, existingLocales);
    }
  },
  methods: {
    pushEmptyRPSCase(locale, caseNumber) {
      const newRpsCase = {
        locale: locale,
        case_number: caseNumber,
        description: "",
        title: "",
        case_question: "",
        description_above_cases: "",
        description_below_cases: "",
        facilitator_instructions: "",
        oid: Math.random()
      };

      this.rpsCases.push(newRpsCase);
    },
    getRPSScenarioInLocale() {
      for (let i = 0; i < this.selectedRPSScenarioLocales.length; i++) {
        if (this.selectedRPSScenarioLocales[i].locale === this.selectedLocale) {
          return this.selectedRPSScenarioLocales[i];
        }
      }

      return null;
    },
    getLanguage(lang) {
      // Hard coded for now for simplicity
      switch (lang) {
        case "en":
          return "ENGLISH";
        case "fi":
          return "SUOMI";
        case "de":
          return "DEUTSCH";
        case "sv":
          return "SVENSKA";
        case "ko":
          return "한국어";
        default:
          return lang;
      }
    },
    getRPSScenarioInSpecifiedLocale(locale) {
      for (let i = 0; i < this.selectedRPSScenarioLocales.length; i++) {
        if (this.selectedRPSScenarioLocales[i].locale === locale) {
          return this.selectedRPSScenarioLocales[i];
        }
      }

      return null;
    },
    disableSave() {
      if (this.rpsCases.length === 0) {
        return true;
      }

      if (this.isValidAllLocales()) {
        return (
          isEqual(this.originalRPSCases, this.rpsCases) &&
          isEqual(this.originalRPSCaseChoices, this.rpsCaseChoices) &&
          isEqual(this.originalSelectedRPSScenarioLocales, this.selectedRPSScenarioLocales)
        );
      } else {
        return true;
      }
    },
    disableCancel() {
      return (
        isEqual(this.originalRPSCases, this.rpsCases) &&
        isEqual(this.originalRPSCaseChoices, this.rpsCaseChoices) &&
        isEqual(this.originalSelectedRPSScenarioLocales, this.selectedRPSScenarioLocales)
      );
    },
    saveButtonHover() {
      if (!this.disableSave) {
        return "";
      }

      if (this.isValidAllLocales()) {
        return "";
        // No changes
      } else {
        return this.$t("check-before-saving");
      }
    },
    isValidAllLocales() {
      const locales = this.getRPSScenarioLocales;

      for (let i = 0; i < locales.length; i++) {
        if (!this.isValid(locales[i])) {
          return false;
        }
      }

      return true;
    },
    getRPSCasesInSpecifiedLocale(locale) {
      if (!this.rpsCases) {
        return [];
      }
      const filtered = this.rpsCases.filter(aCase => aCase.locale === locale);

      return sortby(filtered, [
        o => {
          return o.case_number;
        }
      ]);
    },
    isValid(locale) {
      // Copied code from components
      const rpsScenario = this.getRPSScenarioInSpecifiedLocale(locale);
      if (rpsScenario) {
        if (
          !(
            rpsScenario.start_description.length &&
            rpsScenario.start_title.length &&
            rpsScenario.completion_title.length &&
            rpsScenario.completion_description.length &&
            rpsScenario.short_description.length
          )
        ) {
          return false;
        }
      }

      const allCases = this.getRPSCasesInSpecifiedLocale(locale);
      for (let i = 0; i < allCases.length; i++) {
        const rpsCase = allCases[i];

        if (!(rpsCase.case_question.length && rpsCase.title.length)) {
          return false;
        }
      }

      for (let i = 0; i < this.rpsCaseChoices.length; i++) {
        const rpsCaseChoice = this.rpsCaseChoices[i];
        if (rpsCaseChoice.locale === locale && !rpsCaseChoice.choice_text.length) {
          return false;
        }
      }

      return true;
    },
    addRpsCaseChoice(rpsCase) {
      const rpsCaseChoices = this.getRPSCaseChoicesInLocale(rpsCase.case_number);
      const orderNumber = rpsCaseChoices.length + 1;

      const locales = this.getRPSScenarioLocales;

      // We need to add a new choice for each available language
      for (let i = 0; i < locales.length; i++) {
        const newRpsCaseChoice = {
          locale: locales[i],
          rps_case: rpsCase.rps_case,
          order_number: orderNumber,
          choice_text: "",
          case_number: rpsCase.case_number,
          oid: Math.random()
        };

        this.rpsCaseChoices.push(newRpsCaseChoice);
      }
    },
    addRpsCase() {
      const rpsCases = this.getRPSCasesInLocale;

      const caseNumber = rpsCases.length + 1;

      const locales = this.getRPSScenarioLocales;

      // We need to add new case for each available language
      for (let i = 0; i < locales.length; i++) {
        this.pushEmptyRPSCase(locales[i], caseNumber);
        this.addRpsCaseChoice(this.rpsCases[this.rpsCases.length - 1]);
      }
    },
    getCasesInLocale() {
      if (!this.cases) {
        return [];
      }

      return this.cases.filter(aCase => aCase.locale === this.selectedLocale);
    },
    showDownRpsCaseChoice(rpsCase, rpsCaseChoice) {
      return (
        this.getRPSCaseChoicesInLocale(rpsCase.case_number).length > rpsCaseChoice.order_number
      );
    },
    showUpRpsCaseChoice(rpsCaseChoice) {
      if (rpsCaseChoice.order_number === 1) {
        return false;
      } else {
        return true;
      }
    },
    showDeleteRpsCaseChoice(rpsCase) {
      return this.getRPSCaseChoicesInLocale(rpsCase.case_number).length > 2;
    },
    showAddRpsCaseChoice(rpsCase) {
      return this.getRPSCaseChoicesInLocale(rpsCase.case_number).length < 20;
    },
    showDownRpsCase(rpsCase) {
      return rpsCase.case_number < this.getRPSCasesInLocale.length;
    },
    showUpRpsCase(rpsCase) {
      if (rpsCase.case_number === 1) {
        return false;
      } else {
        return true;
      }
    },
    rpsScenarioOtherLocales() {
      const otherLocales = [];

      for (let i = 0; i < this.selectedRPSScenarioLocales.length; i++) {
        if (this.selectedRPSScenarioLocales[i].locale !== this.selectedLocale) {
          otherLocales.push(this.selectedRPSScenarioLocales[i]);
        }
      }

      return otherLocales;
    },
    rpsCaseOtherLocales(rpsCase) {
      const otherLocales = [];

      for (let i = 0; i < this.rpsCases.length; i++) {
        if (
          this.rpsCases[i].case_number === rpsCase.case_number &&
          this.rpsCases[i].locale !== rpsCase.locale
        ) {
          otherLocales.push(this.rpsCases[i]);
        }
      }

      return otherLocales;
    },
    rpsCaseChoiceOtherLocales(rpsCaseChoice) {
      const otherLocales = [];

      for (let i = 0; i < this.rpsCaseChoices.length; i++) {
        if (
          this.rpsCaseChoices[i].case_number === rpsCaseChoice.case_number &&
          this.rpsCaseChoices[i].order_number === rpsCaseChoice.order_number &&
          this.rpsCaseChoices[i].locale !== rpsCaseChoice.locale
        ) {
          otherLocales.push(this.rpsCaseChoices[i]);
        }
      }

      return otherLocales;
    },
    showDeleteRpsCase() {
      return this.getRPSCasesInLocale.length > 1;
    },
    showAddRpsCase() {
      return (
        !this.modalTemplate &&
        this.ready &&
        this.getRPSScenarioLocales.length &&
        this.getRPSCasesInLocale.length < 100
      );
    },
    showAddRpsCaseFromTemplate() {
      if (!this.showAddRpsCase()) {
        return false;
      }

      // Show if there exists templates currently selected locale
      const index = this.templateCategories.findIndex(c => c.locale === this.selectedLocale);
      const index2 = this.rpsScenariosTemplates.findIndex(c => c.locale === this.selectedLocale);

      return index !== -1 || index2 !== -1;
    },
    getRPSCaseChoicesInSpecifiedLocale(case_number, locale) {
      if (!this.rpsCaseChoices) {
        return [];
      }

      const choicesInLocale = this.rpsCaseChoices.filter(
        aChoice => aChoice.locale === locale && aChoice.case_number === case_number
      );

      return sortby(choicesInLocale, [
        o => {
          return o.order_number;
        }
      ]);
    },
    getRPSCaseChoicesInLocale(case_number) {
      return this.getRPSCaseChoicesInSpecifiedLocale(case_number, this.selectedLocale);
    },
    async selectScenario(scenario) {
      this.showAlert = false;
      this.selectedRPSScenario = scenario;
      this.cases = null;

      const scenarioResp = await getRPSScenario(this.selectedRPSScenario);
      this.selectedRPSScenarioLocales = scenarioResp.data;

      const resp = await getRPSCases(this.selectedRPSScenario);

      this.rpsCases = resp.data;
      this.rpsCases = this.rpsCases.sort((a, b) => {
        return a.case_number - b.case_number;
      });

      const resp2 = await getRPSCaseChoices(this.selectedRPSScenario);
      this.rpsCaseChoices = resp2.data;
      this.rpsCaseChoices = this.rpsCaseChoices.sort((a, b) => {
        return a.order_number - b.order_number;
      });

      this.originalRPSCases = cloneDeep(this.rpsCases);
      this.originalRPSCaseChoices = cloneDeep(this.rpsCaseChoices);
      this.originalSelectedRPSScenarioLocales = cloneDeep(this.selectedRPSScenarioLocales);

      for (let i = 0; i < this.selectedRPSScenarioLocales.length; i++) {
        if (this.selectedRPSScenarioLocales[i].locale === this.$i18n.locale) {
          this.selectedLocale = this.$i18n.locale;
          return;
        }
      }
      // Didn't find locale mathing i18n, let's just pick the first one
      this.selectedLocale = this.selectedRPSScenarioLocales[0].locale;
    },
    changeLocale(locale) {
      this.selectedLocale = locale;
    },
    async addLocaleDialog(newLocale) {
      if (this.rpsCases.length === 0) {
        // No scenario yet
        this.addLocale(newLocale, false);
        return;
      }

      Swal.fire({
        title: this.$t("add-locale"),
        text: this.$t("add-locale-description"),
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: this.$t("confirm-button"),
        denyButtonText: this.$t("deny-button"),
        cancelButtonText: this.$t("cancel-button"),
        allowOutsideClick: false
      }).then(result => {
        if (result.value) {
          // Ok translate
          this.addLocale(newLocale, true);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          // Cancel, do nothing
        } else {
          // Deny, add locale but don't translate
          this.addLocale(newLocale, false);
        }
      });
    },
    async addLocale(newLocale, doTranslate) {
      if (doTranslate) {
        this.translating = true;
        this.ready = false;
        this.translatableItems = 1; // RPS Scenario
        this.translatableItems += this.getRPSCasesInSpecifiedLocale(this.selectedLocale).length;
        this.translatableItems += this.rpsCaseChoices.filter(
          choice => choice.locale === this.selectedLocale
        ).length;
      }

      // Let's create same amount of rps case, choices as there is for other languages:
      const previousLocale = this.selectedLocale;
      this.changeLocale(newLocale);
      const allCases = this.getRPSCasesInSpecifiedLocale(previousLocale);

      for (let i = 0; i < allCases.length; i++) {
        const newCase = cloneDeep(allCases[i]);
        newCase.oid = Math.random();
        newCase.rps_case = null;
        newCase.locale = newLocale;

        if (doTranslate) {
          newCase.title = await this.translateString(previousLocale, newLocale, newCase.title);
          newCase.description = await this.translateString(
            previousLocale,
            newLocale,
            newCase.description
          );
          newCase.case_question = await this.translateString(
            previousLocale,
            newLocale,
            newCase.case_question
          );
          newCase.description_above_cases = await this.translateString(
            previousLocale,
            newLocale,
            newCase.description_above_cases
          );
          newCase.description_below_cases = await this.translateString(
            previousLocale,
            newLocale,
            newCase.description_below_cases
          );
          newCase.facilitator_instructions = await this.translateString(
            previousLocale,
            newLocale,
            newCase.facilitator_instructions
          );
          this.translatedItems++;
          this.rpsCases.push(newCase);
        } else {
          newCase.title = "";
          newCase.description = "";
          newCase.case_question = "";
          newCase.description_above_cases = "";
          newCase.description_below_cases = "";
          newCase.facilitator_instructions = "";
          this.rpsCases.push(newCase);
        }

        const allChoices = this.getRPSCaseChoicesInSpecifiedLocale(
          allCases[i].case_number,
          previousLocale
        );

        for (let j = 0; j < allChoices.length; j++) {
          const newChoice = cloneDeep(allChoices[j]);
          newChoice.locale = newLocale;
          newChoice.id = null;
          newChoice.oid = Math.random();

          if (doTranslate) {
            this.translateString(previousLocale, newLocale, newChoice.choice_text).then(value => {
              this.translatedItems++;
              newChoice.choice_text = value;
              this.rpsCaseChoices.push(newChoice);
            });
          } else {
            newChoice.choice_text = "";
            this.rpsCaseChoices.push(newChoice);
          }
        }
      }

      let newLocaleRpsScenario = null;

      if (doTranslate) {
        this.translatedItems++;
        newLocaleRpsScenario = await this.translateRpsScenario(previousLocale, newLocale);
      } else {
        newLocaleRpsScenario = {
          locale: newLocale,
          start_description: "",
          start_title: "",
          completion_title: "",
          completion_description: "",
          short_description: "",
          begin_page_facilitator_instructions: "",
          last_page_facilitator_instructions: "",
          reporting_facilitator_instructions: "",
          rps_scenario_id: this.selectedRPSScenario ? this.selectedRPSScenario.id : null
        };
      }

      this.selectedRPSScenarioLocales.push(newLocaleRpsScenario);

      this.localeValidityKey++;
      this.translating = false;
      this.translatableItems = 0;
      this.translatedItems = 0;
      this.ready = true;
    },
    cancel() {
      this.rpsCases = cloneDeep(this.originalRPSCases);
      this.rpsCaseChoices = cloneDeep(this.originalRPSCaseChoices);
      this.selectedRPSScenarioLocales = cloneDeep(this.originalSelectedRPSScenarioLocales);

      this.selectedLocale = this.getRPSScenarioLocales[0];
    },
    async save() {
      const request = [];

      request.push({ rps_scenario: this.selectedRPSScenarioLocales });
      request.push({ rps_cases: this.rpsCases });
      request.push({ rps_case_choices: this.rpsCaseChoices });

      try {
        const response = await editRPSScenario(request);

        this.saveError = false;

        this.$router.push(
          "/admin/scenarioeditor/edit/" +
            response[0].rps_scenario_id +
            "/save/true/locale/" +
            this.selectedLocale
        );
      } catch (error) {
        this.saveError = true;
        this.saveErrorMessage = error.message;
      }
    },
    async translateRpsScenario(fromLocale, toLocale) {
      const rpsScenario = this.getRPSScenarioInSpecifiedLocale(fromLocale);

      const translatedRpsScenario = {
        locale: toLocale,
        start_description: await this.translateString(
          fromLocale,
          toLocale,
          rpsScenario.start_description,
          true
        ),
        start_title: await this.translateString(fromLocale, toLocale, rpsScenario.start_title),
        completion_title: await this.translateString(
          fromLocale,
          toLocale,
          rpsScenario.completion_title
        ),
        completion_description: await this.translateString(
          fromLocale,
          toLocale,
          rpsScenario.completion_description
        ),
        short_description: await this.translateString(
          fromLocale,
          toLocale,
          rpsScenario.short_description
        ),
        begin_page_facilitator_instructions: await this.translateString(
          fromLocale,
          toLocale,
          rpsScenario.begin_page_facilitator_instructions
        ),
        last_page_facilitator_instructions: await this.translateString(
          fromLocale,
          toLocale,
          rpsScenario.last_page_facilitator_instructions
        ),
        reporting_facilitator_instructions: await this.translateString(
          fromLocale,
          toLocale,
          rpsScenario.reporting_facilitator_instructions
        )
      };

      return translatedRpsScenario;
    },
    async translateString(fromLocale, toLocale, string, sendToSlack) {
      if (string === null || string.length === 0) {
        return string;
      }
      const text = await translate(fromLocale, toLocale, string, sendToSlack);

      return text.data.translatedText;
    },
    startEditorTimeout() {
      const id = setInterval(async () => {
        const scenarioResp = await getRPSScenario(this.selectedRPSScenario);

        if (scenarioResp.data[0].obsolete) {
          // Uh oh, this scenario is now obsolete -
          // probably someone has edited and saved it
          // on another window

          this.clearInterval();

          Swal.fire({
            text: this.$t("obsolete-scenario"),
            showDenyButton: false,
            confirmButtonText: "OK",
            allowOutsideClick: false
          }).then(result => {
            this.$router.push("/admin/editor");
          });
        }
      }, 30000);

      this.$store.commit({
        type: "setEditorTimer",
        editorTimer: id
      });

      this.$store.dispatch("log", "Editor interval started");
    },
    clearInterval() {
      if (this.$store.state.main.editorTimer) {
        clearInterval(this.$store.state.main.editorTimer);

        this.$store.commit({
          type: "setEditorTimer",
          editorTimer: null
        });

        this.$store.dispatch("log", "Editor interval cleared");
      }
    },
    beforeEnter(el) {
      el.style.opacity = 0;
      el.style.transform = 'translateX(30px)';
    },
    enter(el, done) {
      const animation = el.animate([
        { 
          opacity: 0,
          transform: 'translateX(30px)'
        },
        { 
          opacity: 1,
          transform: 'translateX(0)'
        }
      ], {
        duration: 500,
        easing: 'cubic-bezier(0.55, 0, 0.1, 1)'
      });
      animation.onfinish = done;
    },
    leave(el, done) {
      const animation = el.animate([
        { 
          opacity: 1,
          transform: 'translateX(0)'
        },
        { 
          opacity: 0,
          transform: 'translateX(30px)'
        }
      ], {
        duration: 500,
        easing: 'cubic-bezier(0.55, 0, 0.1, 1)'
      });
      animation.onfinish = done;
    },
    beforeEnterChoice(el) {
      el.style.opacity = 0;
      el.style.transform = 'translateX(30px)';
    },
    enterChoice(el, done) {
      requestAnimationFrame(() => {
        el.style.transition = 'all 500ms cubic-bezier(0.55, 0, 0.1, 1)';
        el.style.opacity = 1;
        el.style.transform = 'translateX(0)';
      });
      setTimeout(done, 500);
    },
    leaveChoice(el, done) {
      el.style.transition = 'all 500ms cubic-bezier(0.55, 0, 0.1, 1)';
      el.style.opacity = 0;
      el.style.transform = 'translateX(30px)';
      setTimeout(done, 500);
    },
    simpleEnter(el, done) {
      el.style.opacity = '0';
      el.style.transform = 'translateX(30px)';
      el.offsetHeight; // Force reflow
      el.style.transition = 'all 300ms ease';
      el.style.opacity = '1';
      el.style.transform = 'translateX(0)';
      setTimeout(done, 300);
    },
    simpleLeave(el, done) {
      el.style.transition = 'all 300ms ease';
      el.style.opacity = '0';
      el.style.transform = 'translateX(30px)';
      setTimeout(done, 300);
    }
  }
};
</script>

<style scoped>
::v-deep .error {
  border-color: red !important;
  border-width: 3px;
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.list-leave-active {
  position: absolute;
}
</style>

<i18n>
  {
    "en": {
      "begin-end": "Text shown in the beginning and end of the simulation",
      "cases": "Text for different cases of the simulation",
      "answer-options": "Answer options",
      "add-option": "Add answer option",
      "add-case": "Add new case",
      "add-case-from-templates": "Add new case from templates",
      "save-successful": "Save successful",
      "save-changes": "Save changes",
      "check-before-saving": "Some required fields are not filled. Please fix the fields marked with red.",
      "cancel-changes": "Cancel all changes",
      "exit-discard": "Exit editor and discard changes",
      "exit": "Exit",
      "choose-locale": "Select simulation language to edit:",
      "add-new-language": "Add new language",
      "add-new-language-hover": "Languages cannot be removed from the simulation",
      "create-new": "Create new simulation",
      "save-error": "Error saving the simulation! Please contact SmoothTeam support at smoothteam@smoothteam.net. Error message:",
      "example": "Example",
      "edit-simulation": "Edit simulation",
      "translating": "Translating",
      "add-locale": "Add locale",
      "add-locale-description": "You can initialize a new language by machine translation from the currently selected language or by using empty fields. Please proofread the machine translation before taking it into use. Once added, the language cannot be removed from the simulation.",
      "confirm-button": "Tee konekäännös",
      "deny-button": "Lisää tyhjät kentät",
      "cancel-button": "Peru",
      "obsolete-scenario": "Simulaatiota on muokattu toisessa selainistunnossa, ja tämä versio on vanhentunut. Napsauta OK ja käynnistä editori uudelleen."
    },
    "fi": {
      "begin-end": "Simulaation alussa ja lopussa näytettävät tekstit",
      "cases": "Simulaation eri tilanteissa näytettävät tekstit",
      "answer-options": "Vastausvaihtoehdot",
      "add-option": "Lisää vastausvaihtoehto",
      "add-case": "Lisää uusi tilanne",
      "add-case-from-templates": "Lisää uusi tilanne kirjastosta",
      "save-successful": "Tallennus onnistui",
      "save-changes": "Tallenna muutokset",
      "check-before-saving": "Osa pakollisista kentistä on tyhjiä. Lisää sisältöä punaisella merkittyihin kenttiin.",
      "cancel-changes": "Peru kaikki muutokset",
      "exit-discard": "Poistu editorista ja peru muutokset",
      "exit": "Poistu",
      "choose-locale": "Valitse simulaation kieli jota muokataan:",
      "add-new-language": "Lisää uusi kieli",
      "add-new-language-hover": "Lisättyä kieltä ei voi poistaa simulaatiosta",
      "create-new": "Luo uusi simulaatio",
      "save-error": "Simulaation tallennus epäonnistui! Ota yhteyttä SmoothTeamin tukeen smoothteam@smoothteam.fi. Virheilmoitus:",
      "example": "Esimerkki",
      "edit-simulation": "Muokkaa simulaatiota",
      "translating": "Käännös käynnissä",
      "add-locale": "Lisää kieli",
      "add-locale-description": "Voit alustaa uuden kielen konekäännöksellä tällä hetkellä valittuna olevasta kielestä tai käyttämällä tyhjiä kenttiä. Tarkistathan konekäännöksen ennen simulaation käyttöönottoa. Lisättyä kieltä ei voi poistaa simulaatiosta.",
      "confirm-button": "Tee konekäännös",
      "deny-button": "Lisää tyhjät kentät",
      "cancel-button": "Peru",
      "obsolete-scenario": "Simulaatiota on muokattu toisessa selainistunnossa, ja tämä versio on vanhentunut. Napsauta OK ja käynnistä editori uudelleen."
    }
  }
</i18n>
